import { SignIn, SignUp } from '@clerk/clerk-react'
import { Ambient } from '@/components/ui/Ambient'
import { FEATURE_ORGANISATIONS } from '@/lib/featureFlags'
import { JoinOrgTabs } from '@/components/shell/JoinOrgPanel'
import { AuthFormFrame } from '@/components/ui/AuthFormFrame'
import { useLocation, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '@/lib/pageTitle'
import { CONTINUE_PARAM, REDIRECT_PARAM, authUrlWithRedirect, locationToPath, safeRedirect } from '@/lib/redirect'

export function SignInPage() {
  usePageTitle('Sign in')
  // ProtectedRoute redirects here with `?redirect_url=` = the page the user was
  // trying to reach (e.g. a shared /library/:id?share=:courseId link), query
  // string and hash included. Honor it so deep links land back on the intended
  // page — and pass it on to /sign-up, since Clerk's "Sign up" link navigates
  // outside the router and would otherwise drop it.
  const [params] = useSearchParams()
  const location = useLocation()
  const legacy = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from
  // An organisation invitation link passes the invited email, so it is already filled in. It is
  // kept for Clerk's create-account step (/sign-in/create), which drops the query — the
  // invitee was asked for their email a second time (7 Oct 2026).
  const email = rememberedEmail(params.get('email'), location.pathname.startsWith('/sign-in/create'))
  const from = safeRedirect(params.get(REDIRECT_PARAM) ?? (legacy ? locationToPath(legacy) : null))
  // ?continue=1 (join links): sign in OR create an account from the same email box.
  // Clerk's create-account step (/sign-in/create…) drops the query, so being on it means the
  // same thing — without this the step had no sign-up to show, Clerk fell back to its hosted
  // sign-in, and a newly invited person looped between the two (7 Oct 2026).
  const createStep = location.pathname.startsWith('/sign-in/create')
  const either = params.get(CONTINUE_PARAM) === '1' || createStep
  // Clerk's own create step left the email EMPTY (19 Oct 2026, JOIN-021): it does not carry
  // <SignIn>'s initialValues — or what was typed (20 Oct, joining with a code) — over. When we know
  // the email, the create step is our own <SignUp> on the same path, whose initialValues fill it.
  const form = FEATURE_ORGANISATIONS && createStep && email ? (
    <SignUp
      initialValues={{ emailAddress: email }}
      routing="path"
      path="/sign-in/create"
      signInUrl={authUrlWithRedirect('/sign-in', from)}
      forceRedirectUrl={from ?? '/'}
    />
  ) : (
    <SignIn
      initialValues={email ? { emailAddress: email } : undefined}
      routing="path"
      path="/sign-in"
      signUpUrl={authUrlWithRedirect('/sign-up', from)}
      forceRedirectUrl={from}
      withSignUp={either}
      signUpForceRedirectUrl={either ? from : undefined}
    />
  )

  // Same ground as the journey — the flat grey made creating an account feel
  // like leaving the product to fill in a form somewhere else.
  return (
    <>
    <Ambient />
    {/* Pinned to the top, not centred (21 Oct 2026): each Clerk step is a different height, so a
        centred page moved the logo, tabs and card up and down on every Continue. */}
    <div className="min-h-screen flex flex-col items-center justify-start px-4 pb-4 relative" style={{ zIndex: 1, paddingTop: 'clamp(32px, 10vh, 96px)' }}>
      <div className="mb-8 text-center">
        {/* learnbee-logo.png is the transparent wordmark; /learnbee.png
            carries a white plate that shows as a box on a tinted ground. */}
        <img src="/learnbee-logo.png" alt="Learnbee" className="h-9 w-auto mx-auto mb-2" />
        <p className="text-gray-500 text-sm">AI-powered course creation</p>
      </div>
      {/* Organisations (dev only): a second way in — join an organisation with a code. */}
      {FEATURE_ORGANISATIONS ? (
        <JoinOrgTabs formLabel={either ? 'Continue' : 'Sign in'} redirectTo={from ?? '/'} continueMode={either}
          formOnly={!!from?.startsWith('/join/org/')} form={<AuthFormFrame onEmail={rememberEmail}>{form}</AuthFormFrame>} />
      ) : (
        <SignIn
          routing="path"
          path="/sign-in"
          signUpUrl={authUrlWithRedirect('/sign-up', from)}
          forceRedirectUrl={from}
        />
      )}
    </div>
    </>
  )
}

const EMAIL_KEY = 'learnbee_invite_email'
/** An email typed on the first step, for the create-account step after it (20 Oct 2026 — joining with a code). */
const rememberEmail = (e: string) => { try { sessionStorage.setItem(EMAIL_KEY, e) } catch { /* private mode */ } }
/** The email from the link, remembered for this tab so the next step can fill it in too. */
function rememberedEmail(fromLink: string | null, createStep: boolean): string | undefined {
  try {
    if (fromLink) { sessionStorage.setItem(EMAIL_KEY, fromLink); return fromLink }
    return createStep ? sessionStorage.getItem(EMAIL_KEY) ?? undefined : undefined
  } catch { return fromLink ?? undefined }
}
